import { GoalHabitsType } from './lib/types'
import { useChartUsery } from './lib/hooks'
import { css } from '@fable/theme'
import pluralize from 'pluralize'
import { GoalHabitsProvider, useGoalHabits } from './lib/ctx'
import { useStats } from 'app/webview/pages/stats_profile/lib/ctx/stats'
import { useStatsPermission } from 'app/webview/pages/stats_profile/lib/hooks'
import { StatsProfileBox } from '../../../shared/stats_profile_box'
import { ReaderInsight } from '../../../shared/ReaderInsight'
import { GoalHabitsTabs } from './GoalHabitsTabs'
import { getName } from 'utils'
import capitalize from 'lodash/capitalize'
import { MonthRadarContent, WeekBarContent } from './charts'
import { useState } from 'react'
import { analytics } from 'segment_analytics'
import { formatK } from '@fable/utils'
import { useStatsTheme } from 'app/webview/pages/stats_profile/theme/ctx'

const Content = () => {
  const [shouldFetch, setShouldFetch] = useState(false)
  const theme = useStatsTheme((s) => s.theme)
  const [type, periodType, weekTotalRead, monthAvgRead, isZeroState] =
    useGoalHabits((s) => [
      s.type,
      s.periodType,
      s.weekTotalRead,
      s.monthAvgRead,
      s.isZeroState,
    ])
  const query = useChartUsery({ enabled: shouldFetch })
  const [yearStatus, stats, isSelfView] = useStats((s) => [
    s.yearStatus,
    s.stats,
    s.isSelfView,
  ])
  const { isStatsOwner, isPlusUser, isAndroid } = useStatsPermission()
  const titleNum = periodType === 'week' ? weekTotalRead : monthAvgRead
  const id = `goal-habits-${type}-${periodType}`
  const statsUserName = getName(stats?.user?.display_name)?.first
  const firstName = statsUserName ? capitalize(statsUserName) : ''

  if (!isPlusUser || isAndroid) return null

  const getTitle = () => {
    switch (type) {
      case 'active_days':
        return pluralize('day', titleNum, true)
      case 'books_finished':
        return pluralize('books', titleNum, true)
      case 'pages_read':
        return `${formatK(titleNum)} ${pluralize('pages', titleNum)}`
    }
  }

  const getZeroStateDescription = () => {
    const periodName = periodType === 'week' ? 'weekly' : 'monthly'

    switch (type) {
      case 'active_days':
        return `Set your ${periodName} reading goal. Then start recording every day you read with the Streaks Tracker in the Home tab.`
      case 'books_finished':
        return `Set your ${periodName} book reading goal. Then add Finished dates to all the books you complete!`
      case 'pages_read':
        return `Set your ${periodName} page count goal. Then start tracking your daily page count with the Streaks Tracker in the Home tab.`
    }
  }

  const getBottomText = () => {
    switch (periodType) {
      case 'week':
        return 'over the last 10 weeks'
      case 'month':
        return 'per month'
    }
  }

  const getInfoText = () => {
    switch (type) {
      case 'active_days':
        return (
          <>
            <b>How do I mark a day as read?</b>
            <br />
            Go to the Home tab and tap the green “I read today” button. Don’t
            see an “I read today” button? Check the Lists section of your
            Profile tab, and add the book to your Currently Reading list.
            <br />
            <br />
            <b>How do I edit what days I read?</b>
            <br />
            Go to Profile &gt; Settings &gt; Edit Streaks Log
          </>
        )
      case 'books_finished':
        return (
          <>
            <b>How do I mark a book as read?</b>
            <br />
            Open the book detail page, and mark it as “Finished.” Be sure to add
            a Finished date!
            <br />
            <br />
            <b>Need to remove a book?</b>
            <br />
            Find the book on your Finished list and tap “...” to update reading
            status. If the book isn’t in your Finished list (but is showing up
            in your stats), re-add it to your Finished list and remove the
            dates.
          </>
        )
      case 'pages_read':
        return (
          <>
            <b>How do I mark pages read?</b>
            <br />
            Go to the Home tab and tap the green “I read today” button. Then
            update how many pages you read using the Progress feature!
          </>
        )
    }
  }

  const getContent = () => {
    if (query.isLoading) {
      return (
        <span
          className={css`
            display: block;
            width: 100%;
            height: 295px;
            position: relative;
            background-color: ${theme.colors.section.bgSecondary};
            border-radius: 16px;
          `}
        />
      )
    }

    if (periodType === 'week') return <WeekBarContent />

    return <MonthRadarContent />
  }

  const queryLoaded = shouldFetch && !query.isIdle && query.isFetched

  return (
    <StatsProfileBox
      id={id}
      data-testid={`stats-${id}`}
      onViewed={() => !shouldFetch && setShouldFetch(true)}
      text={{
        top: `On average, ${isSelfView ? 'you' : firstName} read`,
        main: getTitle(),
        bottom: (
          <span
            className={css`
              display: block;
              white-space: nowrap;
              color: ${theme.colors.section.textSecondary};
              flex-shrink: 0;
            `}
          >
            {getBottomText()}
          </span>
        ),
        infoText: getInfoText(),
      }}
      textAnim={{
        trigger: 'data',
        loaded: queryLoaded,
      }}
      footer={
        queryLoaded &&
        isStatsOwner &&
        isZeroState &&
        yearStatus === 'current' ? (
          <ReaderInsight
            data-testid={`reader-insight-${id}`}
            label="How to get started"
            text={getZeroStateDescription()}
          />
        ) : null
      }
      actions={<GoalHabitsTabs />}
      screenshot={{
        enabled: !isZeroState,
        component:
          periodType === 'week' ? (
            <WeekBarContent skipAnim />
          ) : (
            <MonthRadarContent skipAnim />
          ),
        onClick: () => {
          const period = periodType === 'week' ? 'wow' : 'mom'
          const getType = () => {
            switch (type) {
              case 'active_days':
                return 'days'
              case 'books_finished':
                return 'book'
              case 'pages_read':
                return 'pages'
            }
          }
          const name = `${period}_custom_goals_${getType()}`
          analytics.events.stats.statsShareTap({ type: name })
        },
      }}
    >
      {getContent()}
    </StatsProfileBox>
  )
}

interface Props {
  type: GoalHabitsType
}

export const GoalHabits = ({ ...props }: Props) => (
  <GoalHabitsProvider {...props}>
    <Content />
  </GoalHabitsProvider>
)
